import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './LiquidazioneIvaDettaglioPage.api';
import './Scritture.css';

const MODULO = 'amministrazione.liquidazioni_iva';

type Stato = 'APERTA' | 'DEFINITIVA';
type Direzione = 'DEBITO' | 'CREDITO';

interface LiquidazioneFull {
  id: number;
  societaId: number;
  anno: number;
  periodo: number;
  tipoPeriodo: 'MENSILE' | 'TRIMESTRALE';
  label: string;
  stato: Stato;
  dataInizioPeriodo: string | null;
  dataFinePeriodo: string | null;
  totaleIvaDebito: number | null;
  totaleIvaCredito: number | null;
  saldoPeriodo: number | null;
  creditoIvaPregresso: number | null;
  ivaDaVersare: number | null;
  creditoDaRiportare: number | null;
  dataDefinizione: string | null;
  utenteDefinizioneName: string | null;
  note: string | null;
}

interface Runtime {
  totaleIvaDebito: number;
  totaleIvaCredito: number;
  saldoPeriodo: number;
  creditoIvaPregresso: number;
  ivaDaVersare: number;
  creditoDaRiportare: number;
}

interface DettaglioCodice {
  codiceIvaId: number | null;
  codice: string;
  descrizione: string;
  aliquota: number;
  imponibileDebito: number;
  ivaDebito: number;
  imponibileCredito: number;
  ivaCredito: number;
}

interface RigaIva {
  scritturaId: number;
  scritturaNumero: number | null;
  scritturaAnno: number | null;
  scritturaData: string;
  scritturaDescrizione: string;
  scritturaStato: 'BOZZA' | 'CONFERMATA' | 'STORNATA';
  rigaId: number;
  contoCodice: string;
  contoDescrizione: string;
  codiceIvaCodice: string;
  codiceIvaAliquota: number | null;
  imponibile: number;
  importoIva: number;
  direzione: Direzione;
}

interface ScritturaPeriodo {
  id: number;
  numero: number | null;
  anno: number | null;
  data: string;
  descrizione: string;
  causale: { id: number; codice: string; descrizione: string; gestisceIva: boolean } | null;
  registroContabile: { id: number; codice: string; descrizione: string } | null;
  nominativo: { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null } | null;
  importoTotale: number;
  stato: 'BOZZA' | 'CONFERMATA' | 'STORNATA';
}

interface Blocco { tipo: string; descrizione: string; count?: number }

function nominativoLabel(n: { ragioneSociale?: string | null; nome?: string | null; cognome?: string | null; codice?: string } | null): string {
  if (!n) return '';
  if (n.ragioneSociale) return n.ragioneSociale;
  return [n.nome, n.cognome].filter(Boolean).join(' ').trim() || n.codice || '';
}

export default function LiquidazioneIvaDettaglioPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? parseInt(idParam, 10) : NaN;
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');

  const [liq, setLiq] = useState<LiquidazioneFull | null>(null);
  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [dettaglio, setDettaglio] = useState<DettaglioCodice[]>([]);
  const [righeIva, setRigheIva] = useState<RigaIva[]>([]);
  const [scritture, setScritture] = useState<ScritturaPeriodo[]>([]);
  const [blocchi, setBlocchi] = useState<Blocco[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useBloccoRecordContesto({
    recordSocietaId: liq?.societaId,
    label: liq ? `Liquidazione IVA ${liq.label}` : 'Liquidazione IVA',
  });

  const load = async (silent = false) => {
    if (!Number.isFinite(id)) { setError('ID liquidazione non valido'); setLoading(false); return; }
    if (!silent) setLoading(true);
    setError('');
    try {
      // @api GET /api/liquidazioni-iva/:id -> { success: true, data: {...}, runtime: {...}, dettaglioPerCodiceIva: [...], righeIvaPeriodo: [...], scritturePeriodo: [...], blocchiPreDefinitiva: [...] }
      const res = await apiFetch<{
        success: boolean;
        data: LiquidazioneFull;
        runtime: Runtime;
        dettaglioPerCodiceIva: DettaglioCodice[];
        righeIvaPeriodo: RigaIva[];
        scritturePeriodo: ScritturaPeriodo[];
        blocchiPreDefinitiva: Blocco[];
      }>(`/api/liquidazioni-iva/${id}`);
      setLiq(res.data);
      setRuntime(res.runtime);
      setDettaglio(res.dettaglioPerCodiceIva || []);
      setRigheIva(res.righeIvaPeriodo || []);
      setScritture(res.scritturePeriodo || []);
      setBlocchi(res.blocchiPreDefinitiva || []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [id]);

  const marcaDefinitiva = async () => {
    if (!liq) return;
    if (blocchi.length > 0) {
      window.alert('Risolvi prima i blocchi:\n\n' + blocchi.map(b => `• ${b.descrizione}`).join('\n'));
      return;
    }
    const conferma = window.confirm(
      `Confermare la marcatura DEFINITIVA per ${liq.label}?\n\n` +
      `Totale IVA a debito: ${formatCurrency(runtime?.totaleIvaDebito || 0)}\n` +
      `Totale IVA a credito: ${formatCurrency(runtime?.totaleIvaCredito || 0)}\n` +
      `Saldo periodo: ${formatCurrency(runtime?.saldoPeriodo || 0)}\n` +
      `Credito IVA pregresso: ${formatCurrency(runtime?.creditoIvaPregresso || 0)}\n` +
      `IVA da versare: ${formatCurrency(runtime?.ivaDaVersare || 0)}\n` +
      `Credito da riportare: ${formatCurrency(runtime?.creditoDaRiportare || 0)}\n\n` +
      `Da questo momento le scritture IVA del periodo NON saranno più modificabili.`,
    );
    if (!conferma) return;
    setSaving(true);
    try {
      // @api PATCH /api/liquidazioni-iva/:id/definitiva -> { success: true, data: {...} }
      await apiFetch(`/api/liquidazioni-iva/${id}/definitiva`, { method: 'PATCH' });
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore nella marcatura DEFINITIVA');
    } finally {
      setSaving(false);
    }
  };

  const riapri = async () => {
    if (!liq) return;
    if (!window.confirm(`Riaprire la liquidazione ${liq.label}?\n\nLe scritture IVA del periodo torneranno modificabili e lo snapshot dei totali verrà azzerato.`)) return;
    setSaving(true);
    try {
      // @api PATCH /api/liquidazioni-iva/:id/riapri -> { success: true, data: {...} }
      await apiFetch(`/api/liquidazioni-iva/${id}/riapri`, { method: 'PATCH' });
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore nella riapertura');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className="bop-empty-state">Caricamento…</div></Layout>;
  if (error) return <Layout><div className="bop-inline-error">{error}</div></Layout>;
  if (!liq) return <Layout><div className="bop-empty-state">Liquidazione non trovata.</div></Layout>;

  const isDef = liq.stato === 'DEFINITIVA';
  // Per i totali mostrati: snapshot se DEFINITIVA, runtime altrimenti
  const totali = isDef ? {
    totaleIvaDebito: liq.totaleIvaDebito || 0,
    totaleIvaCredito: liq.totaleIvaCredito || 0,
    saldoPeriodo: liq.saldoPeriodo || 0,
    creditoIvaPregresso: liq.creditoIvaPregresso || 0,
    ivaDaVersare: liq.ivaDaVersare || 0,
    creditoDaRiportare: liq.creditoDaRiportare || 0,
  } : (runtime || { totaleIvaDebito: 0, totaleIvaCredito: 0, saldoPeriodo: 0, creditoIvaPregresso: 0, ivaDaVersare: 0, creditoDaRiportare: 0 });

  return (
    <Layout>
      <div className="scritture-contabili liq-dettaglio">
        <BopBackLink to="/amministrazione/liquidazioni-iva" label="Torna alle liquidazioni IVA" />
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>
              Liquidazione IVA — {liq.label}
              {isDef && <span className="bop-badge bop-badge--success sc-badge-inline">DEFINITIVA</span>}
              {!isDef && <span className="bop-badge bop-badge--warning sc-badge-inline">APERTA</span>}
            </h2>
            <p>
              Periodo:{' '}
              {liq.dataInizioPeriodo && new Date(liq.dataInizioPeriodo).toLocaleDateString('it-IT')} →{' '}
              {liq.dataFinePeriodo && new Date(liq.dataFinePeriodo).toLocaleDateString('it-IT')}
              {isDef && liq.dataDefinizione && (
                <> · chiusa il <strong>{new Date(liq.dataDefinizione).toLocaleDateString('it-IT')}</strong>
                {liq.utenteDefinizioneName && <> da {liq.utenteDefinizioneName}</>}</>
              )}
            </p>
          </div>
          <div className="bop-flex">
            {canEdit && isDef && (
              <button className="bop-btn bop-btn-secondary" onClick={riapri} disabled={saving}>Riapri</button>
            )}
            {canEdit && !isDef && (
              <button className="bop-btn bop-btn-primary" onClick={marcaDefinitiva} disabled={saving || blocchi.length > 0}>
                {saving ? 'Salvo…' : 'Marca DEFINITIVA'}
              </button>
            )}
          </div>
        </div>

        {/* RIEPILOGO TOTALI */}
        <div className="bop-card liq-riepilogo">
          <div className="sc-card-header"><h3>Riepilogo totali {!isDef && <span className="liq-runtime-badge">(anteprima runtime)</span>}</h3></div>
          <div className="liq-totali-grid">
            <div className="liq-totale-cell">
              <div className="liq-totale-label">IVA a debito (vendite)</div>
              <div className="liq-totale-valore liq-totale-valore--debito">{formatCurrency(totali.totaleIvaDebito)}</div>
            </div>
            <div className="liq-totale-cell">
              <div className="liq-totale-label">IVA a credito (acquisti)</div>
              <div className="liq-totale-valore liq-totale-valore--credito">{formatCurrency(totali.totaleIvaCredito)}</div>
            </div>
            <div className="liq-totale-cell">
              <div className="liq-totale-label">Saldo periodo</div>
              <div className={`liq-totale-valore ${totali.saldoPeriodo >= 0 ? 'liq-totale-valore--debito' : 'liq-totale-valore--credito'}`}>{formatCurrency(totali.saldoPeriodo)}</div>
            </div>
            <div className="liq-totale-cell">
              <div className="liq-totale-label">Credito IVA pregresso</div>
              <div className="liq-totale-valore liq-totale-valore--credito">{formatCurrency(totali.creditoIvaPregresso)}</div>
            </div>
            <div className="liq-totale-cell liq-totale-cell--highlight">
              <div className="liq-totale-label">IVA da versare</div>
              <div className="liq-totale-valore liq-totale-valore--debito-strong">{formatCurrency(totali.ivaDaVersare)}</div>
            </div>
            <div className="liq-totale-cell liq-totale-cell--highlight">
              <div className="liq-totale-label">Credito da riportare</div>
              <div className="liq-totale-valore liq-totale-valore--credito-strong">{formatCurrency(totali.creditoDaRiportare)}</div>
            </div>
          </div>
          {!isDef && runtime && liq.saldoPeriodo != null && Math.abs(runtime.saldoPeriodo - liq.saldoPeriodo) > 0.01 && (
            <p className="liq-warning">
              ⚠ Lo snapshot precedente (saldo {formatCurrency(liq.saldoPeriodo)}) differisce dai valori runtime correnti.
            </p>
          )}
        </div>

        {/* BLOCCHI PRE-DEFINITIVA */}
        {!isDef && blocchi.length > 0 && (
          <div className="bop-card liq-blocchi">
            <div className="sc-card-header"><h3>Da risolvere prima della DEFINITIVA</h3></div>
            <ul className="liq-blocchi-lista">
              {blocchi.map((b, i) => (
                <li key={i}>
                  <span className="liq-blocco-tipo">{b.tipo}</span>
                  {b.descrizione}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* DETTAGLIO PER CODICE IVA */}
        <div className="bop-card">
          <div className="sc-card-header"><h3>Dettaglio per codice IVA</h3></div>
          {dettaglio.length === 0 ? (
            <div className="bop-empty-state">Nessuna riga IVA nel periodo.</div>
          ) : (
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th className="bop-text-right">Aliquota</th>
                    <th className="bop-text-right">Imponibile debito</th>
                    <th className="bop-text-right">IVA debito</th>
                    <th className="bop-text-right">Imponibile credito</th>
                    <th className="bop-text-right">IVA credito</th>
                  </tr>
                </thead>
                <tbody>
                  {dettaglio.map((d, i) => (
                    <tr key={i}>
                      <td className="sc-codice">{d.codice}</td>
                      <td>{d.descrizione}</td>
                      <td className="bop-text-right">{d.aliquota.toFixed(2)}%</td>
                      <td className="bop-text-right">{d.imponibileDebito > 0 ? formatCurrency(d.imponibileDebito) : '—'}</td>
                      <td className="bop-text-right liq-iva-debito">{d.ivaDebito > 0 ? formatCurrency(d.ivaDebito) : '—'}</td>
                      <td className="bop-text-right">{d.imponibileCredito > 0 ? formatCurrency(d.imponibileCredito) : '—'}</td>
                      <td className="bop-text-right liq-iva-credito">{d.ivaCredito > 0 ? formatCurrency(d.ivaCredito) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* SCRITTURE DEL PERIODO */}
        <div className="bop-card">
          <div className="sc-card-header"><h3>Scritture del periodo ({scritture.length})</h3></div>
          {scritture.length === 0 ? (
            <div className="bop-empty-state">Nessuna scrittura nel periodo.</div>
          ) : (
            <div className="bop-table-wrapper">
              <table className="bop-table bop-table--clickable">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>N° / Anno</th>
                    <th>Registro</th>
                    <th>Causale</th>
                    <th>Descrizione</th>
                    <th>Nominativo</th>
                    <th className="bop-text-right">Importo</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {scritture.map(s => (
                    <tr key={s.id} onClick={() => navigate(`/amministrazione/scritture-contabili/${s.id}`)}>
                      <td className="sc-data">{new Date(s.data).toLocaleDateString('it-IT')}</td>
                      <td className="sc-numero">{s.numero != null ? `${s.numero}/${s.anno}` : <span className="bop-text-muted">bozza</span>}</td>
                      <td>{s.registroContabile ? <strong>{s.registroContabile.codice}</strong> : '—'}</td>
                      <td>{s.causale ? <span className="sc-causale">{s.causale.codice}</span> : <span className="bop-text-muted">libera</span>}</td>
                      <td>{s.descrizione}</td>
                      <td>{nominativoLabel(s.nominativo) || <span className="bop-text-muted">—</span>}</td>
                      <td className="bop-text-right sc-importo">{formatCurrency(s.importoTotale)}</td>
                      <td>
                        <span className={`bop-badge ${
                          s.stato === 'CONFERMATA' ? 'bop-badge--success' :
                          s.stato === 'STORNATA' ? 'bop-badge--warning' :
                          'bop-badge--neutral'
                        }`}>
                          {s.stato}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {liq.note && (
          <div className="bop-card">
            <div className="sc-card-header"><h3>Note</h3></div>
            <p>{liq.note}</p>
          </div>
        )}
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
